/**
 * Live-traffic feed — opens the repo's `live-traces` SSE stream and drives the
 * board's request-flow overlay. The server streams the aggregate OTel service
 * graph (per-service-pair request rate); we map each edge's absolute rate onto a
 * dot spawn rate keyed by service pair, which the overlay joins to board edges.
 *
 * No model needed here: the join key is the normalized service name, computed
 * the same way on both sides (`trafficEdgeKey`).
 */
import { useEffect } from "react";
import {
  trafficEdgeKey,
  normServiceName,
  flowSpawnRate,
  type LiveTrafficStore,
  type LiveStatsStore,
  type LiveHistoryStore,
  type LiveFlowsStore,
  type LiveStats,
  type EdgeStat,
  type NodeStat,
  type LatencyStat,
  type HistoryPoint,
  type ObservedOrdering,
} from "@archlang/app-shell/board";
import { API_BASE } from "../api/client.js";

interface RawEdge {
  readonly fromSvc: string;
  readonly toSvc: string;
  /** Total calls over the source's lookback window (kept for the details panel). */
  readonly count?: number;
  /** ABSOLUTE request rate (req/sec) — the server computes it from the window. */
  readonly perSec?: number;
  /** RED extras (present only when the source sampled raw traces). */
  readonly errorRate?: number;
  readonly latencyMs?: LatencyStat;
  readonly sampled?: number;
}

interface RawNode {
  readonly svc: string;
  readonly perSec?: number;
  readonly errorRate?: number;
  readonly latencyMs?: LatencyStat;
  readonly sampled?: number;
}

/** One history point per edge + node for this frame, keyed like the stores. */
export function historyPointsFromSnapshot(
  edges: readonly RawEdge[],
  nodes: readonly RawNode[],
  at: number,
): Array<readonly [string, HistoryPoint]> {
  const pts: Array<readonly [string, HistoryPoint]> = [];
  for (const e of edges) {
    pts.push([
      trafficEdgeKey(e.fromSvc, e.toSvc),
      {
        at,
        perSec: e.perSec ?? 0,
        errorRate: e.errorRate,
        p50: e.latencyMs?.p50,
        p95: e.latencyMs?.p95,
        p99: e.latencyMs?.p99,
      },
    ]);
  }
  for (const n of nodes) {
    pts.push([
      normServiceName(n.svc),
      {
        at,
        perSec: n.perSec ?? 0,
        errorRate: n.errorRate,
        p50: n.latencyMs?.p50,
        p95: n.latencyMs?.p95,
        p99: n.latencyMs?.p99,
      },
    ]);
  }
  return pts;
}

/**
 * Map the server's stats snapshot to the LiveStatsStore shape — edges keyed by
 * `trafficEdgeKey`, nodes by `normServiceName`, matching the overlay's join so a
 * board module id resolves the OTel service stat. Composable: errorRate/latency/
 * sampled pass through only when present.
 */
export function statsFromSnapshot(
  edges: readonly RawEdge[],
  nodes: readonly RawNode[],
): LiveStats {
  const edgeMap = new Map<string, EdgeStat>();
  for (const e of edges) {
    edgeMap.set(trafficEdgeKey(e.fromSvc, e.toSvc), {
      from: e.fromSvc,
      to: e.toSvc,
      perSec: e.perSec ?? 0,
      count: e.count,
      errorRate: e.errorRate,
      latencyMs: e.latencyMs,
      sampled: e.sampled,
    });
  }
  const nodeMap = new Map<string, NodeStat>();
  for (const n of nodes) {
    nodeMap.set(normServiceName(n.svc), {
      perSec: n.perSec ?? 0,
      errorRate: n.errorRate,
      latencyMs: n.latencyMs,
      sampled: n.sampled,
    });
  }
  return { edges: edgeMap, nodes: nodeMap };
}

/**
 * Map the server's aggregate service-graph edges to per-edge dot spawn rates.
 *
 * Sizes each edge off its ABSOLUTE request rate (req/sec) through the shared log
 * soft-knee compressor — the overlay density tracks real traffic (honest and
 * sparse when quiet, legible when busy) instead of the old relative-to-max guess
 * that emitted ~30 dots/s on a 1.6 req/s edge. A dot is NOT one request; zero
 * rate → no entry (no fabricated dots on idle edges).
 */
export function ratesFromEdges(edges: readonly RawEdge[]): Map<string, number> {
  const rates = new Map<string, number>();
  for (const e of edges) {
    const rate = flowSpawnRate(e.perSec ?? 0);
    if (rate <= 0) continue;
    rates.set(trafficEdgeKey(e.fromSvc, e.toSvc), rate);
  }
  return rates;
}

export function useLiveTraffic(
  slug: string | undefined,
  repo: string | undefined,
  store: LiveTrafficStore,
  statsStore?: LiveStatsStore,
  historyStore?: LiveHistoryStore,
  flowsStore?: LiveFlowsStore,
): void {
  useEffect(() => {
    if (!slug || !repo) return;
    // No live feed where EventSource is absent (SSR / jsdom tests) — the overlay
    // is a live-browser-only concern; skip cleanly rather than throwing on mount.
    if (typeof EventSource === "undefined") return;
    const url = `${API_BASE}/api/ws/${encodeURIComponent(slug)}/r/${encodeURIComponent(repo)}/live-traces`;
    const es = new EventSource(url, { withCredentials: true });

    es.addEventListener("edges", (ev) => {
      try {
        const { edges, nodes, flows, at } = JSON.parse((ev as MessageEvent).data) as {
          edges: RawEdge[];
          nodes?: RawNode[];
          flows?: ObservedOrdering[];
          at?: number;
        };
        store.setRates(ratesFromEdges(edges));
        statsStore?.setStats(statsFromSnapshot(edges, nodes ?? []));
        historyStore?.push(historyPointsFromSnapshot(edges, nodes ?? [], at ?? Date.now()));
        flowsStore?.setFlows(flows ?? []);
      } catch {
        /* ignore a malformed frame; the next poll refreshes */
      }
    });
    es.addEventListener("idle", () => {
      store.setRates(null);
      statsStore?.setStats(null);
      flowsStore?.setFlows([]);
    });
    // On transient error the browser auto-reconnects; keep the last frame.

    return () => {
      es.close();
      store.setRates(null);
      statsStore?.setStats(null);
      flowsStore?.setFlows([]);
    };
  }, [slug, repo, store, statsStore, historyStore, flowsStore]);
}
